import {compareName} from '../../srcCommon/helpers/localeHelpers';
import {AppRoutesPrefixesEnum} from '../components/appRoutesPrefixes';
import {AccountModel} from '../models/api/accountModel';
import {AccountsSetupGuideTasksEnum} from '../models/api/accountsSetupGuideModel';
import {ContactSourcesEnum, parseContactUid} from '../models/api/contactModel';
import {CrmSyncModel, CrmSyncSourcesEnum} from '../models/api/crmSyncModel';
import {commonProviderDomains, extractDomainFromEmail} from './domainHelpers';

/*
 * Constants.
 */

const NewlyCreatedAccountTimeCutoff = 5000; // ms.

// Discover Front Accounts is always marked as finished, as the Pendo guide will always
// be visible on first visit. Further context in discoverFrontAccountsTask.tsx
const isDiscoverFrontAccountsComplete = true;

export const accountsLearnMoreLink =
  'https://help.front.com/t/h7h1h2s/customer-beta-understanding-front-accounts';

export const accountsCustomFieldsLearnMoreLink = 'https://help.front.com/t/x2h1yqq/custom-account-fields';

export const accountsPrepareCsvLearnMoreLink =
  'https://help.front.com/t/35h1yq9/upload-accounts-with-a-csv#prepare-your-csv-file';

export const setupAccountsUrl = '/contacts-manager/accounts';

/*
 * Helpers.
 */

export function buildAccountPublicLink(host: string, accountId: number) {
  return `${host}/${AppRoutesPrefixesEnum.CONTACTS_MANAGER_ACCOUNTS}/${accountId}`;
}

export function extractNonGenericAccountDomainsFromContactUids(
  contactUids: ReadonlyArray<string>,
): ReadonlyArray<string> {
  return contactUids
    .map(parseContactUid)
    .filter((contactUid) => contactUid[0] === ContactSourcesEnum.EMAIL)
    .map((contactUid) => extractDomainFromEmail(contactUid[1]))
    .filter((domain) => domain && !commonProviderDomains.includes(domain))
    .sort((a, b) => compareName(a, b));
}

export function wasAccountRecentlyCreated(account: AccountModel) {
  return Date.now() - account.createdAt < NewlyCreatedAccountTimeCutoff;
}

export function buildAccountCrmExternalUrl(account: AccountModel, crmSync: CrmSyncModel) {
  if (!account.externalInfo || !crmSync.externalUrl) {
    return undefined;
  }
  if (account.externalInfo.source !== crmSync.source) {
    return undefined;
  }
  if (crmSync.source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS) {
    return buildMicrosoftDynamicsExternalUrl(crmSync.externalUrl, account.externalInfo.id);
  }
  return `${crmSync.externalUrl}/${account.externalInfo.id}`;
}

export function buildMicrosoftDynamicsExternalUrl(baseUrl: string, id: string) {
  return `${baseUrl}/main.aspx?pagetype=entityrecord&etn=account&id=${id}`;
}

export function computeAccountsSetupGuideCompletedTasks(
  completedTasks: ReadonlyArray<AccountsSetupGuideTasksEnum>,
) {
  const isAddAccountCustomFieldComplete = completedTasks.includes(
    AccountsSetupGuideTasksEnum.ADD_ACCOUNT_CUSTOM_FIELD,
  );
  const isAddAccountComplete = completedTasks.includes(AccountsSetupGuideTasksEnum.ADD_ACCOUNT);
  const isAddAccountRuleComplete = completedTasks.includes(AccountsSetupGuideTasksEnum.ADD_ACCOUNT_RULE);

  return {
    isDiscoverFrontAccountsComplete,
    isAddAccountCustomFieldComplete,
    isAddAccountComplete,
    isAddAccountRuleComplete,
  };
}
